import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useUser } from "../context/UserProvider";
import { auth } from "../lib/firebase";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from "../i18n";

export default function Onboarding() {
  const { createProfile, settings } = useUser();
  const user = auth.currentUser;
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(user?.email || settings?.email || "");
  const [phone, setPhone] = useState(user?.phoneNumber || settings?.phoneNumber || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [firstErr, setFirstErr] = useState("");
  const [lastErr, setLastErr] = useState("");
  const [contactErr, setContactErr] = useState("");
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(getCurrentLanguage());

  // Determine if user signed in with email or phone (used for read-only fields)
  const signedInWithEmail = !!user?.email;
  const signedInWithPhone = !!user?.phoneNumber;

  useEffect(() => {
    setEmail((e) => e || user?.email || settings?.email || "");
    setPhone((p) => p || user?.phoneNumber || settings?.phoneNumber || "");
  }, [settings?.email, settings?.phoneNumber, user?.email, user?.phoneNumber]);

  async function handleSubmit() {
    setError("");
    setStatus("");
    setFirstErr("");
    setLastErr("");
    setContactErr("");

    if (!firstName.trim()) setFirstErr(t('onboarding.firstNameRequired'));
    if (!lastName.trim()) setLastErr(t('onboarding.lastNameRequired'));
    const hasEmail = !!email.trim();
    const hasPhone = !!phone.trim();
    if (!hasEmail && !hasPhone) setContactErr(t('onboarding.emailOrPhoneRequired'));
    if (!firstName.trim() || !lastName.trim() || (!hasEmail && !hasPhone)) return;

    setLoading(true);
    try {
      await createProfile({ firstName, lastName, email, phoneNumber: hasPhone ? `+${phone.replace(/[^0-9]/g, "")}` : "" });
      setStatus(t('onboarding.profileSaved'));
    } catch (err) {
      // Translate known UserProvider error messages
      const msg = err?.message || '';
      const translatedError =
        msg.includes('No authenticated user') ? t('auth.noAuthenticatedUser') :
          msg.includes('Auth not ready') ? t('auth.authNotReady') :
            msg.includes('email or phone') ? t('auth.emailOrPhoneRequired') :
              msg || t('onboarding.couldNotSaveProfile');
      setError(translatedError);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-slate-900 flex items-center justify-center p-6 relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm hover:shadow-md transition"
        >
          <Globe size={16} className="text-naimGreen" />
          <span className="text-naimGreen font-medium text-sm">
            {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.nativeName?.slice(0, 8) || 'EN'}
          </span>
          <ChevronDown size={14} className="text-naimGreen" />
        </button>
        {showLanguageDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50 max-h-80 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setSelectedLanguage(lang.code);
                  changeLanguage(lang.code);
                  setShowLanguageDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left flex justify-between items-center hover:bg-green-50 transition ${lang.code === selectedLanguage ? 'bg-green-50 text-naimGreen' : 'text-gray-700'}`}
              >
                <span>{lang.nativeName}</span>
                {lang.code === selectedLanguage && <Check size={16} className="text-naimGreen" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-full max-w-xl bg-white border border-gray-200 rounded-2xl p-7 shadow-lg">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-slate-800">{t('onboarding.profileSettings')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('onboarding.shareBasicsToSecure')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <input
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-naimGreen"
              placeholder={t('onboarding.firstName')}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFirstErr("");
              }}
            />
            {firstErr && <p className="text-xs text-red-500 mt-1">{firstErr}</p>}
          </div>
          <div>
            <input
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-naimGreen"
              placeholder={t('onboarding.lastName')}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setLastErr("");
              }}
            />
            {lastErr && <p className="text-xs text-red-500 mt-1">{lastErr}</p>}
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              {t('auth.email')} {signedInWithEmail ? "" : t('onboarding.emailOptional')}
            </label>
            <input
              className={`w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-naimGreen ${signedInWithEmail ? 'bg-slate-50 text-slate-600' : 'bg-white'}`}
              placeholder={t('auth.email')}
              value={email}
              onChange={(e) => !signedInWithEmail && setEmail(e.target.value)}
              readOnly={signedInWithEmail}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              {t('auth.phone')} {signedInWithPhone ? "" : t('onboarding.phoneOptional')}
            </label>
            <PhoneInput
              country={"se"}
              value={phone}
              onChange={(v) => {
                if (!signedInWithPhone) {
                  setPhone(v);
                  setContactErr("");
                }
              }}
              disabled={signedInWithPhone}
              inputClass={`!w-full !h-11 !pl-12 !pr-3 !border !border-slate-200 !rounded-lg !focus:outline-none !focus:border-naimGreen ${signedInWithPhone ? '!bg-slate-50 !text-slate-600' : ''}`}
              buttonClass={`!border !border-slate-200 !rounded-l-lg ${signedInWithPhone ? '!bg-slate-50' : '!bg-white'}`}
              containerClass="!w-full"
              dropdownClass="phone-dropdown"
            />
            {contactErr && <p className="text-xs text-red-500 mt-1">{contactErr}</p>}
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-naimGreen hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition disabled:opacity-60"
          >
            {loading ? t('onboarding.saving') : t('onboarding.save')}
          </button>
          <button
            onClick={() => auth.signOut().catch(() => { })}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition"
          >
            {t('onboarding.signOut')}
          </button>
        </div>

        {error && <p className="text-red-500 mt-3 text-sm">{error}</p>}
        {status && <p className="text-green-600 mt-2 text-sm">{status}</p>}
      </div>
    </div>
  );
}
